import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	effect,
	inject,
	input,
	OnInit,
	output,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';

type ApiListResponse<T> = { results: T[] };
type ViewItem<T> = { value: T; id: unknown; label: string };

@Component({
	selector: 'app-generic-mat-autocomplete',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatAutocompleteModule,
		MatOptionModule,
		MatChipsModule,
		MatIconModule,
	],
	template: ` @if (multiple()) {
			<mat-form-field appearance="outline" class="w-100">
				<mat-label>{{ placeholder() }}</mat-label>

				<mat-chip-grid #chipGrid>
					@for (item of selectedItems(); track item.id) {
						<mat-chip-row (removed)="removeItem(item)">
							{{ item.label }}
							<button matChipRemove>
								<i class="fa-solid fa-xmark"></i>
							</button>
						</mat-chip-row>
					}
					<input
						type="text"
						matInput
						#chipInput
						[placeholder]="selectedItems().length === 0 ? placeholder() : ''"
						[formControl]="searchControl"
						[matAutocomplete]="auto"
						[matChipInputFor]="chipGrid"
					/>
				</mat-chip-grid>
				<mat-autocomplete
					#auto="matAutocomplete"
					[displayWith]="displayWith"
					(optionSelected)="onOptionSelected($event.option.value); chipInput.value = ''"
				>
					@for (item of items(); track item.id) {
						<mat-option [value]="item.value">
							{{ item.label }}
						</mat-option>
					}
				</mat-autocomplete>

				@if (required() && searchControl.touched && searchControl.hasError('required')) {
					<mat-error>To pole jest wymagane</mat-error>
				}
			</mat-form-field>
		} @else {
			<mat-form-field appearance="outline" class="w-100">
				<input
					type="text"
					matInput
					[placeholder]="placeholder()"
					[formControl]="searchControl"
					[matAutocomplete]="auto"
				/>

				<mat-autocomplete
					#auto="matAutocomplete"
					[displayWith]="displayWith"
					(optionSelected)="onOptionSelected($event.option.value)"
				>
					@for (item of items(); track item.id) {
						<mat-option [value]="item.value">
							{{ item.label }}
						</mat-option>
					}
				</mat-autocomplete>

				@if (required() && searchControl.touched && searchControl.hasError('required')) {
					<mat-error>To pole jest wymagane</mat-error>
				}
			</mat-form-field>
		}`,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenericMatAutocompleteComponent<T extends Record<string, unknown>> implements OnInit {
	readonly apiUrl = input.required<string>();
	readonly apiSearchParam = input.required<string>();
	readonly idKey = input<string>('id');
	readonly placeholder = input<string>('Wyszukaj');
	readonly required = input<boolean>(false);
	readonly multiple = input<boolean>(false);
	readonly selectedItems = signal<ViewItem<T>[]>([]);

	readonly displayKeys = input<Array<keyof T>>([]);
	readonly displaySeparator = input<string>(' ');

	readonly selected = output<T | null>();
	readonly selectedMultiple = output<T[]>();

	readonly searchControl = new FormControl<T | string>(null);

	readonly responseMapper = input<(response: unknown) => T[]>();
	readonly showOptionsOnFocus = input<boolean>(false);
	private readonly results = signal<T[]>([]);
	// eslint-disable-next-line @typescript-eslint/member-ordering
	readonly items = computed<ViewItem<T>[]>(() => {
		const data = this.results() ?? [];
		const idKey = this.idKey();
		const keys = this.displayKeys() ?? [];
		const sep = this.displaySeparator() ?? ' ';

		return data.map((v) => {
			const id = v?.[idKey];

			const label = keys.length
				? keys
						.map((k) => (v?.[k] ?? '') as string)
						.filter(Boolean)
						.join(sep)
				: ((v?.name ?? String(id ?? '')) as string);

			return { value: v, id, label };
		});
	});

	private readonly http = inject(HttpClient);
	private readonly destroyRef = inject(DestroyRef);

	constructor() {
		effect(() => {
			const isReq = this.required();
			this.searchControl.setValidators(isReq ? [Validators.required] : []);
			this.searchControl.updateValueAndValidity({ emitEvent: false });
		});
	}

	ngOnInit(): void {
		if (this.showOptionsOnFocus()) {
			this.search('').subscribe((list) => {
				this.results.set(list);
			});
		}

		this.searchControl.valueChanges
			.pipe(
				map((v) => (typeof v === 'string' ? v.trim() : '')),
				tap((q) => {
					if (q.length < 3) this.results.set([]);
				}),
				filter((q) => q.length >= 3),
				debounceTime(300),
				distinctUntilChanged(),
				switchMap((q) => this.search(q)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((list) => this.results.set(list));
	}

	displayWith = (value: T | string | null): string => {
		if (!value || typeof value === 'string') {
			return (value ?? '') as string;
		}
		const idKey = this.idKey();
		const id = value[idKey];
		const found = this.items().find((i) => i.id === id);
		return found?.label ?? '';
	};

	onOptionSelected(value: T): void {
		if (this.multiple()) {
			const idKey = this.idKey();
			const id = value[idKey];
			if (!this.selectedItems().some((item) => item.id === id)) {
				this.selectedItems.update((items) => [
					...items,
					{
						value,
						id,
						label: this.displayKeys()
							.map((key) => value[key] as string)
							.filter((val) => val != null)
							.join(' '),
					},
				]);

				this.selectedMultiple.emit(this.selectedItems().map((item) => item.value));
			}

			// Reset the autocomplete value
			// Do to an open issue this doesn't actually work right now
			// and instead this is handled by the input.value = '' in
			// the template
			// https://github.com/angular/material2/issues/8214
			this.searchControl.setValue('');
		} else {
			this.selected.emit(value);
		}
	}

	removeItem(item: ViewItem<T>): void {
		this.selectedItems.update((items) => items.filter((i) => i.id !== item.id));
		this.selectedMultiple.emit(this.selectedItems().map((item) => item.value));
	}

	private search(query: string) {
		const url = `${this.apiUrl()}?${this.apiSearchParam()}=${encodeURIComponent(query)}`;
		return this.http.get<ApiListResponse<T>>(url).pipe(
			map((res) => {
				const mapper = this.responseMapper();
				if (mapper) {
					return mapper(res);
				}

				if (res && Array.isArray(res.results)) {
					return res.results as T[];
				}

				return Array.isArray(res) ? (res as T[]) : [];
			}),
		);
	}
}
